<template>
  <div class="question-card" :class="{ 'preview-mode': previewMode }">
    <div class="question-header">
      <div class="question-badges">
        <el-tag :class="kindTagClass" size="mini">{{ kindLabel }}</el-tag>
        <el-tag :type="difficultyTagType" size="mini">{{ question.difficulty || 'medium' }}</el-tag>
        <el-tag v-if="question.generation_source === 'ai_generated'" type="info" size="mini">
          <i class="el-icon-magic-stick"></i> AI
        </el-tag>
        <el-tag v-if="question.status === 'draft'" type="warning" size="mini">Draft</el-tag>
      </div>
      <div class="question-actions" v-if="!previewMode">
        <el-button type="text" icon="el-icon-edit" @click="$emit('edit', question)">Edit</el-button>
        <el-button type="text" icon="el-icon-delete" class="delete-btn" @click="$emit('delete', question)">Delete</el-button>
      </div>
      <div class="preview-actions" v-else>
        <el-button type="text" icon="el-icon-close" @click="$emit('remove')">Remove</el-button>
      </div>
    </div>

    <div class="question-content">
      <div class="question-text">
        <strong>Q:</strong> {{ question.question }}
      </div>

      <div class="options-list">
        <div
          v-for="(option, index) in question.options"
          :key="index"
          class="option-item"
          :class="{ 'correct': isCorrectOption(index) }"
        >
          <span class="option-label">{{ optionLabels[index] }}</span>
          <span class="option-text">{{ option }}</span>
          <i v-if="isCorrectOption(index)" class="el-icon-check correct-icon"></i>
        </div>
      </div>

      <div class="explanation" v-if="question.explanation">
        <strong>Explanation:</strong>
        <span>{{ question.explanation }}</span>
      </div>
    </div>

    <div class="question-footer" v-if="question.tags && question.tags.length > 0">
      <el-tag v-for="tag in question.tags" :key="tag" size="mini" type="info">{{ tag }}</el-tag>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TestBankQuestionCard',
  props: {
    question: {
      type: Object,
      required: true
    },
    previewMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      optionLabels: ['A', 'B', 'C', 'D']
    }
  },
  computed: {
    difficultyTagType() {
      const types = {
        easy: 'success',
        medium: 'warning',
        hard: 'danger'
      }
      return types[this.question.difficulty] || 'info'
    },
    questionKind() {
      // Records without question_kind default to comprehension (matches backend stats fallback).
      return this.question.question_kind || 'comprehension'
    },
    kindLabel() {
      return this.questionKind === 'application' ? 'Application' : 'Comprehension'
    },
    kindTagClass() {
      return this.questionKind === 'application' ? 'kind-tag application' : 'kind-tag comprehension'
    }
  },
  methods: {
    isCorrectOption(index) {
      const correctAnswer = this.question.correct_answer
      return correctAnswer === this.optionLabels[index]
    }
  }
}
</script>

<style scoped>
.question-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  transition: box-shadow 0.2s;
}

.question-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.question-card.preview-mode {
  border-style: dashed;
  background: #fafafa;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.question-badges {
  display: flex;
  gap: 8px;
}

.kind-tag.comprehension {
  background: #ecf5ff;
  border-color: #d9ecff;
  color: #409eff;
}

.kind-tag.application {
  background: #f3e7f7;
  border-color: #e7d6ee;
  color: #9b59b6;
}

.question-actions .delete-btn {
  color: #f56c6c;
}

.question-content {
  margin-bottom: 12px;
}

.question-text {
  font-size: 15px;
  line-height: 1.6;
  color: #303133;
  margin-bottom: 12px;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 14px;
}

.option-item.correct {
  background: #e1f3d8;
  border: 1px solid #67c23a;
}

.option-label {
  font-weight: 600;
  color: #606266;
  min-width: 20px;
}

.option-text {
  flex: 1;
  color: #303133;
}

.correct-icon {
  color: #67c23a;
  font-weight: bold;
}

.explanation {
  padding: 12px;
  background: #fdf6ec;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}

.explanation strong {
  color: #e6a23c;
  margin-right: 8px;
}

.question-footer {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}
</style>
